export interface ArticleSection {
  heading: string;
  paragraphs: string[];
  list?: string[];
}

export interface ArticleFaq {
  q: string;
  a: string;
}

export interface PillarArticle {
  slug: string;
  key: string;
  image: string;
  readTime: string;
  sections: ArticleSection[];
  faq: ArticleFaq[];
}

export const pillarArticles: PillarArticle[] = [
  {
    slug: 'basetao-spreadsheet-guide',
    key: 'guide',
    image:
      'https://readdy.ai/api/search-image?query=A%20clean%20open%20spreadsheet%20on%20a%20laptop%20screen%20next%20to%20a%20paper%20notebook%20and%20pen%20on%20a%20warm%20oak%20desk%2C%20soft%20morning%20light%2C%20tidy%20minimal%20workspace%2C%20muted%20earthy%20tones%2C%20airy%20editorial%20photography%2C%20no%20text&width=800&height=600&seq=article-guide&orientation=landscape',
    readTime: '8 min read',
    sections: [
      {
        heading: 'Introduction',
        paragraphs: [
          'Shopping through a Chinese purchasing agent like Basetao opens the door to a huge range of products, from sneakers and clothing to accessories and everyday essentials. But once your cart starts to fill up, keeping track of every item, seller, price and QC photo can quickly become overwhelming.',
          'That is where a Basetao spreadsheet comes in. It is a simple, organized way to record everything you are buying, so you always know what you have ordered, where it is and how much it cost. This guide walks you through everything you need to build and use one effectively.',
        ],
      },
      {
        heading: 'What is Basetao?',
        paragraphs: [
          'Basetao is a shopping agent that helps international buyers purchase items from Chinese online platforms such as Taobao, 1688 and Weidian. It acts as a middle layer: you paste a product link, the agent buys the item for you, stores it in their warehouse and then ships it to your country. Along the way you can request QC (quality check) photos to confirm the item looks right before it is shipped.',
        ],
      },
      {
        heading: 'What is a Basetao Spreadsheet?',
        paragraphs: [
          'A Basetao spreadsheet is a shared document, usually built in Google Sheets or Excel, that lists every product you are interested in or have ordered through the agent. It typically records the product link, the seller, the price, the size or color, the QC status and the shipping progress.',
          'Think of it as your personal control center for a Basetao haul. Instead of juggling dozens of browser tabs and chat messages, you keep everything in one place that you can update and review at a glance.',
        ],
      },
      {
        heading: 'Why use a Basetao Spreadsheet?',
        paragraphs: [
          'A well-maintained spreadsheet saves time and prevents costly mistakes. Here are the main benefits:',
        ],
        list: [
          'Keep track of many items at once, especially useful for large hauls.',
          'Compare prices and sellers before you commit to a purchase.',
          'Record QC results so you can decide quickly whether to keep or return an item.',
          'Monitor warehouse and shipping status without searching through chat logs.',
          'Calculate a realistic total cost including agent fees and shipping.',
        ],
      },
      {
        heading: 'What to include in your spreadsheet',
        paragraphs: [
          'The exact columns you need depend on how you shop, but these fields are useful for almost everyone:',
        ],
        list: [
          'Product link, the original URL so you can reopen the listing anytime.',
          'Seller name, to compare quality between different stores.',
          'Price, in the local currency and your own currency.',
          'Size, color and quantity, to avoid ordering the wrong variant.',
          'QC status and photo link, to track which items passed inspection.',
          'Warehouse status, arrived, stored or ready to ship.',
          'Shipping method and tracking number, for the final delivery.',
        ],
      },
      {
        heading: 'How to structure your spreadsheet',
        paragraphs: [
          'A clear column layout makes your spreadsheet easy to scan and filter. A practical starting structure uses one row per item and a handful of columns: product name, product link, seller, price, size and color, QC status, and overall status such as ordered, in warehouse, or shipped.',
        ],
      },
      {
        heading: 'Tips for staying organized',
        paragraphs: [
          'Small habits make a big difference when your haul grows. Keep the original product link because listings can change or disappear, color-code rows by status to see progress at a glance, and update the sheet right after each action instead of relying on memory.',
        ],
      },
      {
        heading: 'Common mistakes to avoid',
        paragraphs: [
          'The most common mistakes are skipping QC, forgetting to note the exact size or color, and not tracking shipping costs until the final total becomes a surprise. Using unclear product names and leaving the sheet outdated also make it far less useful.',
        ],
      },
    ],
    faq: [
      {
        q: 'Is a Basetao spreadsheet the same as the official Basetao site?',
        a: 'No. It is an independent organizational tool and is not affiliated with or part of the official Basetao website unless explicitly stated.',
      },
      {
        q: 'Do I need a spreadsheet for a small order?',
        a: 'Not strictly, but even a few items become easier to manage when the links, prices and QC status are all in one place.',
      },
      {
        q: 'Which tool should I use to build the spreadsheet?',
        a: 'Google Sheets and Excel are both popular. Google Sheets is easy to access from any device and simple to share.',
      },
    ],
  },
  {
    slug: 'basetao-review',
    key: 'review',
    image:
      'https://readdy.ai/api/search-image?query=A%20warm%20editorial%20flat%20lay%20of%20a%20five%20star%20rating%20concept%20made%20of%20paper%20cards%20beside%20a%20smartphone%20and%20coffee%20on%20a%20linen%20tablecloth%2C%20soft%20golden%20morning%20light%2C%20muted%20neutral%20palette%2C%20calm%20trustworthy%20mood%2C%20no%20text&width=800&height=600&seq=article-review&orientation=landscape',
    readTime: '7 min read',
    sections: [
      {
        heading: 'Introduction',
        paragraphs: [
          'Basetao has become a well-known name for shoppers who want to buy products from Chinese platforms and have them shipped internationally. This review looks at how the service works, what it does well and where it has limitations, so you can decide if it fits your needs.',
        ],
      },
      {
        heading: 'How Basetao works',
        paragraphs: [
          'You paste a product link into your account, the agent purchases it on your behalf and it is delivered to a local warehouse. From there you can request quality check photos, combine multiple items and choose a shipping method for international delivery.',
        ],
      },
      {
        heading: 'What shoppers like',
        paragraphs: [
          'Most users appreciate the wide access to products that are otherwise hard to buy internationally, the option to request QC photos before shipping, and the ability to consolidate many items into one package to save on shipping.',
        ],
        list: [
          'Access to Taobao, 1688 and Weidian sellers.',
          'QC photo service to verify items before shipping.',
          'Consolidation to combine multiple orders.',
          'A clear workflow for tracking purchases.',
        ],
      },
      {
        heading: 'What to watch out for',
        paragraphs: [
          'Agent fees, domestic shipping and international freight all add to the final cost, so the item price is not the whole story. Quality and sizing can also vary by seller, which is why requesting QC photos is recommended for important purchases.',
        ],
      },
      {
        heading: 'Is Basetao worth it?',
        paragraphs: [
          'For shoppers who want variety and are comfortable managing the extra steps, Basetao can be a practical option. New users should start with a small order, learn the workflow and always factor in the full cost before committing.',
        ],
      },
    ],
    faq: [
      {
        q: 'Is Basetao a legitimate service?',
        a: 'Basetao is a well-known shopping agent used by many international buyers. As with any agent, do your own research and start with a small order.',
      },
      {
        q: 'Does Basetao charge fees?',
        a: 'Shopping agents typically charge a service or handling fee on top of the item price, plus domestic and international shipping. Confirm the full cost before paying.',
      },
      {
        q: 'Can I get my money back if something goes wrong?',
        a: 'Refund policies vary by seller and situation. Keep records and contact the agent as soon as an issue appears.',
      },
    ],
  },
  {
    slug: 'basetao-shipping-cost',
    key: 'shipping',
    image:
      'https://readdy.ai/api/search-image?query=A%20tidy%20stack%20of%20kraft%20cardboard%20parcels%20with%20a%20tape%20roll%20and%20scissors%20on%20a%20warm%20wooden%20table%2C%20soft%20daylight%2C%20minimal%20shipping%20themed%20editorial%20photography%2C%20muted%20earth%20tones%2C%20clean%20composition%2C%20no%20readable%20labels&width=800&height=600&seq=article-shipping&orientation=landscape',
    readTime: '6 min read',
    sections: [
      {
        heading: 'Introduction',
        paragraphs: [
          'Shipping cost is one of the biggest factors in the final price when you buy through an agent. Understanding how it is calculated helps you avoid surprises and pick the best method for your order.',
        ],
      },
      {
        heading: 'What affects shipping cost',
        paragraphs: [
          'The main factors are weight, the actual or volumetric size of the package, the destination country and the shipping line you choose. Volumetric weight matters for light but bulky items like shoes and jackets.',
        ],
        list: [
          'Actual weight and volumetric weight.',
          'Package dimensions and the number of items.',
          'Destination country and import rules.',
          'The shipping line, express, standard or economy.',
        ],
      },
      {
        heading: 'Common shipping methods',
        paragraphs: [
          'Express lines are faster but more expensive, while economy lines cost less but take longer. Some methods include better tracking or insurance, which can be worth the small extra cost for valuable hauls.',
        ],
      },
      {
        heading: 'How to lower shipping cost',
        paragraphs: [
          'Consolidating several items into one package usually reduces the total freight. Choosing a slightly slower line, removing bulky packaging and requesting rehearsal shipping so the exact weight is known can also help.',
        ],
      },
      {
        heading: 'Estimating your cost',
        paragraphs: [
          'A reliable estimate comes from the total weight and the chosen line. Keep a simple note of item weights in your spreadsheet so you can predict the final figure before you commit.',
        ],
      },
    ],
    faq: [
      {
        q: 'Why is volumetric weight higher than actual weight?',
        a: 'Carriers charge for the space a package occupies, so light but large items can cost more than their actual weight suggests.',
      },
      {
        q: 'Should I always pick the cheapest shipping line?',
        a: 'Not always. Cheaper lines may be slower or have less tracking. Balance cost against speed and reliability.',
      },
      {
        q: 'Does consolidation always save money?',
        a: 'Usually yes, because you pay one combined shipment instead of several separate ones, but compare the estimate before confirming.',
      },
    ],
  },
  {
    slug: 'basetao-warehouse',
    key: 'warehouse',
    image:
      'https://readdy.ai/api/search-image?query=Neatly%20organized%20storage%20shelves%20with%20labeled%20cardboard%20boxes%20and%20parcels%20in%20a%20bright%20modern%20warehouse%2C%20soft%20natural%20light%2C%20clean%20minimal%20logistics%20scene%2C%20muted%20neutral%20palette%2C%20orderly%20composition%2C%20no%20readable%20text&width=800&height=600&seq=article-warehouse&orientation=landscape',
    readTime: '5 min read',
    sections: [
      {
        heading: 'Introduction',
        paragraphs: [
          'The warehouse is where your items wait after the agent buys them and before they are shipped to you. Knowing how it works helps you track orders and plan consolidation.',
        ],
      },
      {
        heading: 'What happens at the warehouse',
        paragraphs: [
          'When an item arrives, the agent logs it against your account and it is stored until you decide what to do next. You can request QC photos, ask for storage, or submit the item for shipping.',
        ],
      },
      {
        heading: 'Storage time and limits',
        paragraphs: [
          'Agents usually offer free storage for a limited period and may charge after that. Check the current policy so you can consolidate and ship within the free window.',
        ],
      },
      {
        heading: 'Consolidating your items',
        paragraphs: [
          'Once several items have arrived, you can combine them into one package. This is when packaging is reduced where possible, and the combined weight is used to calculate international shipping.',
        ],
      },
      {
        heading: 'Tracking warehouse status',
        paragraphs: [
          'Your account dashboard shows the status of each item, such as purchased, arrived or stored. Recording these statuses in your own spreadsheet keeps everything clear in one place.',
        ],
      },
    ],
    faq: [
      {
        q: 'How long can items stay in the warehouse?',
        a: 'There is usually a free storage period, after which a small fee may apply. Check the latest policy in your account.',
      },
      {
        q: 'Can I see my items before shipping?',
        a: 'Yes, requesting QC photos lets you verify the item condition before it is shipped.',
      },
      {
        q: 'What if an item arrives damaged?',
        a: 'Report it to the agent as soon as you notice, ideally with the QC photos as evidence.',
      },
    ],
  },
  {
    slug: 'basetao-qc',
    key: 'qc',
    image:
      'https://readdy.ai/api/search-image?query=A%20magnifying%20glass%20resting%20on%20a%20folded%20garment%20beside%20a%20smartphone%20on%20a%20clean%20desk%2C%20soft%20daylight%2C%20minimal%20quality%20inspection%20themed%20editorial%20photography%2C%20muted%20warm%20tones%2C%20sharp%20focus%2C%20no%20text&width=800&height=600&seq=article-qc&orientation=landscape',
    readTime: '5 min read',
    sections: [
      {
        heading: 'Introduction',
        paragraphs: [
          'Quality check, or QC, is the step where the agent photographs your item so you can confirm it looks correct before shipping. Using QC well catches problems while they are still cheap to fix.',
        ],
      },
      {
        heading: 'What QC photos show',
        paragraphs: [
          'QC photos typically show the item from several angles, close-ups of details, labels and any visible flaws. For clothing they help confirm size, color and material, while for shoes they show the sole, stitching and logos.',
        ],
      },
      {
        heading: 'What to check in QC photos',
        paragraphs: [
          'Look for obvious defects, wrong sizes or colors, missing parts and differences from the listing. For branded items, compare logos and details against what you expect.',
        ],
        list: [
          'Correct color, size and model.',
          'Visible stitching, glue or scratch defects.',
          'Logo and label accuracy.',
          'Packaging and accessories included.',
        ],
      },
      {
        heading: 'What to do if there is a problem',
        paragraphs: [
          'If the photos reveal an issue, contact the agent right away to request an exchange or return before the item ships. Acting early is much easier than dealing with a problem after delivery.',
        ],
      },
    ],
    faq: [
      {
        q: 'Is QC always free?',
        a: 'Basic QC photos are often free or low cost, but detailed photo sets may have a small fee. Confirm before ordering.',
      },
      {
        q: 'Can I request more QC photos?',
        a: 'Usually yes, you can ask for additional angles or close-ups for a small fee.',
      },
      {
        q: 'Should I QC every item?',
        a: 'For low-cost basics it is optional, but for expensive or branded items QC is strongly recommended.',
      },
    ],
  },
  {
    slug: 'basetao-sellers',
    key: 'sellers',
    image:
      'https://readdy.ai/api/search-image?query=A%20cozy%20shop%20front%20window%20display%20with%20neatly%20arranged%20clothing%20and%20accessories%2C%20warm%20inviting%20lighting%2C%20minimal%20retail%20storefront%20editorial%20photography%2C%20muted%20neutral%20tones%2C%20clean%20composition%2C%20no%20readable%20text&width=800&height=600&seq=article-sellers&orientation=landscape',
    readTime: '6 min read',
    sections: [
      {
        heading: 'Introduction',
        paragraphs: [
          'The quality of your haul depends heavily on the sellers you choose. Learning to evaluate stores helps you avoid poor quality and find reliable sources for the items you want.',
        ],
      },
      {
        heading: 'How to spot a reliable seller',
        paragraphs: [
          'Look for stores with a longer history, a high number of completed sales and generally positive feedback. Product photos that are clearly original, rather than reused from elsewhere, are also a good sign.',
        ],
        list: [
          'Store age and number of sales.',
          'Overall feedback and recent reviews.',
          'Detailed, original product photos.',
          'Responsive communication.',
        ],
      },
      {
        heading: 'Reading feedback carefully',
        paragraphs: [
          'Sort reviews by recent and read a mix of positive and negative ones. Look for repeated complaints about sizing, color accuracy or quality rather than focusing on a single bad review.',
        ],
      },
      {
        heading: 'Comparing similar sellers',
        paragraphs: [
          'The same product is often sold by many stores at different prices. Compare photos, descriptions and reviews side by side, and record your shortlist in your spreadsheet before deciding.',
        ],
      },
      {
        heading: 'Building a trusted list',
        paragraphs: [
          'Over time you will build a personal list of sellers that work for you. Keep their names and links in your spreadsheet so future hauls are faster and more predictable.',
        ],
      },
    ],
    faq: [
      {
        q: 'Do higher prices always mean better quality?',
        a: 'Not necessarily. Compare photos and reviews rather than assuming price equals quality.',
      },
      {
        q: 'Can the agent help me find sellers?',
        a: 'The agent mainly purchases links you provide, but your own research and community lists are the best starting point.',
      },
      {
        q: 'What if a seller sends the wrong item?',
        a: 'Use QC photos to catch mistakes and request an exchange or return before shipping.',
      },
    ],
  },
  {
    slug: 'basetao-coupons',
    key: 'coupons',
    image:
      'https://readdy.ai/api/search-image?query=A%20small%20paper%20price%20tag%20and%20discount%20coupon%20resting%20on%20a%20warm%20beige%20surface%20beside%20a%20pair%20of%20scissors%2C%20soft%20daylight%2C%20minimal%20savings%20themed%20editorial%20photography%2C%20muted%20tones%2C%20clean%20simple%20composition%2C%20no%20readable%20text&width=800&height=600&seq=article-coupons&orientation=landscape',
    readTime: '4 min read',
    sections: [
      {
        heading: 'Introduction',
        paragraphs: [
          'Coupons and discount codes can lower the cost of shopping through an agent, but they are not always easy to find. This guide explains where to look and how to use them honestly.',
        ],
      },
      {
        heading: 'Where to find discounts',
        paragraphs: [
          'Check the platform where you shop for seller promotions, seasonal sales and platform vouchers. Some agents also run their own campaigns or fee reductions from time to time.',
        ],
      },
      {
        heading: 'What to verify before using a code',
        paragraphs: [
          'Confirm the expiry date, minimum spend and which products the code applies to. A code that looks good may not apply to your specific items, so read the terms before relying on it.',
        ],
      },
      {
        heading: 'A realistic approach to savings',
        paragraphs: [
          'Coupons are a nice bonus but should not be the main reason to shop. Focus on the total cost including fees and shipping, and treat any discount as extra rather than expected.',
        ],
      },
    ],
    faq: [
      {
        q: 'Are Basetao discount codes always available?',
        a: 'No. Promotions change frequently and may not be available at any given time.',
      },
      {
        q: 'Should I trust third-party coupon sites?',
        a: 'Be cautious with unofficial sites. Prefer offers you can see directly on the platform or from the agent.',
      },
      {
        q: 'Do coupons apply to shipping too?',
        a: 'Usually they apply to items or fees rather than international freight, but this varies by promotion.',
      },
    ],
  },
  {
    slug: 'basetao-safety',
    key: 'safety',
    image:
      'https://readdy.ai/api/search-image?query=A%20simple%20shield%20shaped%20padlock%20resting%20on%20a%20soft%20neutral%20background%20with%20a%20blurred%20laptop%20in%20the%20background%2C%20soft%20daylight%2C%20minimal%20online%20safety%20themed%20editorial%20photography%2C%20calm%20trustworthy%20mood%2C%20muted%20tones%2C%20no%20text&width=800&height=600&seq=article-safety&orientation=landscape',
    readTime: '6 min read',
    sections: [
      {
        heading: 'Introduction',
        paragraphs: [
          'When you shop through an agent, a little caution goes a long way. This page covers practical steps to protect your account, your payments and your purchases.',
        ],
      },
      {
        heading: 'Protecting your account',
        paragraphs: [
          'Use a strong, unique password and enable any available two-factor authentication. Always access your account through the official website rather than links from emails or messages you did not expect.',
        ],
      },
      {
        heading: 'Avoiding fake websites',
        paragraphs: [
          'Scammers sometimes create lookalike sites to capture login details. Check the web address carefully, look for the correct domain and avoid logging in through links from unknown sources.',
        ],
        list: [
          'Type the official address directly into your browser.',
          'Verify the domain before entering any details.',
          'Ignore unsolicited messages asking for your password.',
          'Report suspicious links instead of clicking them.',
        ],
      },
      {
        heading: 'Payment and refunds',
        paragraphs: [
          'Use the payment methods offered by the platform and keep records of every transaction. If something goes wrong, contact the agent with clear details and evidence such as QC photos.',
        ],
      },
      {
        heading: 'A note on guarantees',
        paragraphs: [
          'No third party can guarantee that every order will arrive perfectly or that every seller is reliable. Treat safety as a set of good habits rather than a promise, and always do your own checks.',
        ],
      },
    ],
    faq: [
      {
        q: 'Is Basetao safe to use?',
        a: 'It is a well-known agent used by many buyers, but safety also depends on your own habits such as using a strong password and avoiding fake sites.',
      },
      {
        q: 'How do I spot a fake website?',
        a: 'Check the exact domain, look for unusual requests and avoid links from unknown messages.',
      },
      {
        q: 'What should I do if I think I have been scammed?',
        a: 'Stop using the site, change your password and contact the official support channel with any evidence you have.',
      },
    ],
  },
  {
    slug: 'basetao-vs-superbuy',
    key: 'vs',
    image:
      'https://readdy.ai/api/search-image?query=Two%20cleanly%20separated%20halves%20of%20a%20flat%20lay%2C%20one%20warm%20terracotta%20and%20one%20cool%20sage%20green%2C%20with%20a%20small%20balance%20scale%20in%20the%20center%20on%20a%20light%20surface%2C%20soft%20daylight%2C%20minimal%20comparison%20themed%20editorial%20photography%2C%20no%20text&width=800&height=600&seq=article-vs&orientation=landscape',
    readTime: '7 min read',
    sections: [
      {
        heading: 'Introduction',
        paragraphs: [
          'Basetao and Superbuy are two popular shopping agents that offer a similar core service. Comparing their workflow, fees and features helps you choose the one that fits how you shop.',
        ],
      },
      {
        heading: 'How the services compare',
        paragraphs: [
          'Both agents let you paste a product link, purchase the item and ship it internationally with optional QC photos and consolidation. The differences come down to pricing structure, platform features and personal preference.',
        ],
      },
      {
        heading: 'Fees and pricing',
        paragraphs: [
          'Each agent charges a service or handling fee plus domestic and international shipping. Because fees change over time and vary by item, it is worth comparing the total quote for your specific haul rather than relying on a fixed rule.',
        ],
      },
      {
        heading: 'Features to look for',
        paragraphs: [
          'Look at the quality of QC photos, how easy consolidation is, available shipping lines and the clarity of the account dashboard. A smoother workflow can save more time than a slightly lower fee.',
        ],
        list: [
          'QC photo quality and cost.',
          'Consolidation and rehearsal shipping options.',
          'Shipping line variety and tracking.',
          'Ease of the order and warehouse dashboard.',
        ],
      },
      {
        heading: 'Which should you choose?',
        paragraphs: [
          'There is no single right answer. Start with a small order on the agent whose workflow feels clearest to you, then compare the actual cost and experience before scaling up.',
        ],
      },
    ],
    faq: [
      {
        q: 'Which agent is cheaper?',
        a: 'It depends on the item, the shipping line and current fees. Compare a full quote for your specific order.',
      },
      {
        q: 'Can I use both agents?',
        a: 'Yes, many shoppers try both and settle on the one that works best for them.',
      },
      {
        q: 'Do both offer QC photos?',
        a: 'Both offer some form of quality check, but the details and cost can differ.',
      },
    ],
  },
  {
    slug: 'basetao-beginner-guide',
    key: 'beginner',
    image:
      'https://readdy.ai/api/search-image?query=A%20small%20compass%20and%20a%20folded%20paper%20map%20on%20a%20warm%20wooden%20desk%20beside%20a%20smartphone%2C%20soft%20morning%20light%2C%20minimal%20beginning%20journey%20themed%20editorial%20photography%2C%20muted%20neutral%20tones%2C%20clean%20composition%2C%20no%20readable%20text&width=800&height=600&seq=article-beginner&orientation=landscape',
    readTime: '6 min read',
    sections: [
      {
        heading: 'Introduction',
        paragraphs: [
          'If you are new to shopping through an agent, the process can feel unfamiliar at first. This beginner guide breaks it down into simple steps so you can place your first order with confidence.',
        ],
      },
      {
        heading: 'Step one: find a product',
        paragraphs: [
          'Start with a product you want and copy its link from the source platform. Keep the link safe, because you will need it to place the order and to reopen the listing later.',
        ],
      },
      {
        heading: 'Step two: place the order',
        paragraphs: [
          'Paste the link into your agent account, choose the size, color and quantity, and pay for the item plus any agent fee. The agent then purchases it on your behalf.',
        ],
      },
      {
        heading: 'Step three: QC and warehouse',
        paragraphs: [
          'When the item arrives at the warehouse, request QC photos to confirm it looks right. Once you are happy, you can leave it in storage while other items arrive.',
        ],
        list: [
          'Paste the product link.',
          'Choose size, color and quantity.',
          'Pay for the item and agent fee.',
          'Request QC photos when it arrives.',
          'Consolidate and choose a shipping line.',
        ],
      },
      {
        heading: 'Step four: ship internationally',
        paragraphs: [
          'Consolidate your items into one package, pick a shipping line and pay the freight. You will receive a tracking number to follow the delivery to your door.',
        ],
      },
      {
        heading: 'A tip for your first haul',
        paragraphs: [
          'Keep your first order small and record each step in a simple spreadsheet. This makes the learning curve gentler and sets you up for smoother hauls in the future.',
        ],
      },
    ],
    faq: [
      {
        q: 'Is it hard to use a shopping agent?',
        a: 'The first order takes a little learning, but the process becomes routine after one or two hauls.',
      },
      {
        q: 'What should I buy first?',
        a: 'Start with something low cost and low risk so you can learn the workflow without much downside.',
      },
      {
        q: 'Do I need to track everything myself?',
        a: 'A simple spreadsheet helps a lot, especially once you order several items at once.',
      },
    ],
  },
];

export function getArticleBySlug(slug: string | undefined): PillarArticle | undefined {
  return pillarArticles.find((article) => article.slug === slug);
}